'use client';

import { useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { WebViewScreen } from '@diritto/common/app/webview-header.constant';
import { RollingAddButton } from 'app-components/rolling/RollingAddButton';
import { RollingHeader } from 'app-components/rolling/RollingHeader';
import { RollingViewer } from 'app-components/rolling/RollingViewer';
import { DesktopContentWidth, DesktopGlobalMenuWidth, isPC } from 'constants/layout';
import { useAuthState } from 'contexts/AuthProvider';
import { RollingProvider } from 'contexts/RollingProvider';
import { useGetRandomRollingsInfiniteQuery } from 'queries/relay/useGetRandomRollingsInfiniteQuery';
import { useGetRelayRollingQuery } from 'queries/relay/useGetRelayRollingQuery';
import { BlockContents } from 'ui-components/common/BlockContents';
import { FullPageLoading } from 'ui-components/common/Loading';
import { MutableHeaderFixedLayout } from 'ui-components/layout/MutableHeaderFixedLayout';
import { MutableHeaderLayout } from 'ui-components/layout/MutableHeaderLayout';
import { useInAppHeader } from 'ui-hooks/useInAppHeader';
import { useInAppStatusBar } from 'ui-hooks/useInAppStatusBar';
import { AccessBlockCode } from 'utils/checkAccessBlock';
import { showToast } from 'utils/modal';

export const RollingPage = ({ rollingId, isApp }: { rollingId: number; isApp?: boolean }) => {
  useInAppHeader({ screen: WebViewScreen.RollingDetail, title: '롤링' });
  useInAppStatusBar(WebViewScreen.RollingDetail);
  const { isLoaded, isLoggedIn, user, includeAdult } = useAuthState();

  const router = useRouter();
  const { data, isLoading } = useGetRelayRollingQuery(rollingId, true);
  const randomRollingsInfiniteQuery = useGetRandomRollingsInfiniteQuery(rollingId, {
    includeAdult,
    tag: data?.rolling?.tag,
  });

  const rolling = data?.rolling;

  useEffect(() => {
    if (isLoaded && !isLoading && !rolling) {
      setTimeout(() => {
        showToast('삭제된 롤링이에요.');
        router.replace(`/rollings`);
      }, 1000);
    }
  }, [isLoaded, isLoading, rolling, router]);

  if (isLoaded && !isLoggedIn && !isLoading && rolling?.isAdult) {
    return (
      <MutableHeaderLayout noBottomPadding isApp={isApp}>
        <RollingHeader />
        <BlockContents accessBlockCode={AccessBlockCode.AuthorizationAdultRequired} />
      </MutableHeaderLayout>
    );
  }

  if (isLoaded && !user?.isAdult && !isLoading && rolling?.isAdult) {
    return (
      <MutableHeaderLayout noBottomPadding isApp={isApp}>
        <RollingHeader />
        <BlockContents accessBlockCode={AccessBlockCode.AdultRequired} />
      </MutableHeaderLayout>
    );
  }

  if (isLoading || !isLoaded || !rolling) {
    return (
      <MutableHeaderLayout isApp={isApp}>
        <RollingHeader />
        <FullPageLoading />
      </MutableHeaderLayout>
    );
  }

  return (
    <MutableHeaderFixedLayout title="롤링" noBottomPadding isApp={isApp}>
      <RollingHeader />

      {!data?.rolling && <FullPageLoading />}

      <RollingProvider rollingsQuery={randomRollingsInfiniteQuery}>
        {rolling && <RollingViewer rollingId={rolling.id} />}
      </RollingProvider>

      <div className="fixed bottom-[var(--header-nav-bar-height)] right-0 left-0 flex justify-center pointer-events-none z-[var(--z-index-bottom-window-wrapper)]">
        <div
          className={`flex justify-end w-full pointer-events-none ${isPC ? `max-w-[${DesktopContentWidth + DesktopGlobalMenuWidth}px]` : ''}`}>
          <div className="pointer-events-auto p-0 pr-4 pb-4">
            <RollingAddButton />
          </div>
        </div>
      </div>
    </MutableHeaderFixedLayout>
  );
};
